import { ResponseError } from '../api-contracts';

/** A photo whose file is no longer on the device: sending it again can't succeed. */
export class MissingPhotoError extends Error {
  constructor(photoId: string) {
    super(`Photo not found on this device: ${photoId}`);
    this.name = 'MissingPhotoError';
  }
}

/** A photo upload the server answered with an error status. */
export class UploadStatusError extends Error {
  constructor(readonly status: number) {
    super(`Upload failed with status ${status}`);
    this.name = 'UploadStatusError';
  }
}

// 401 is a sign-in that needs refreshing, 408 and 429 ask to try again later, and
// 426 is fixed by updating the app, after which the Inspection sends without Retry.
const UNCOUNTED_CLIENT_ERRORS = new Set([401, 408, 426, 429]);

const isCountedStatus = (status: number) =>
  status >= 400 && status < 500 && !UNCOUNTED_CLIENT_ERRORS.has(status);

/**
 * Whether a send or photo-upload failure counts towards Needs attention (#11590, SY-6):
 * a rejection retrying won't fix, or a photo file missing on the device. Network
 * errors, timeouts, server errors, 401, 408, 426 and 429 don't count.
 */
export function isCountedFailure(error: unknown): boolean {
  if (error instanceof MissingPhotoError) return true;
  if (error instanceof ResponseError) {
    return isCountedStatus(error.response.status);
  }
  if (error instanceof UploadStatusError) return isCountedStatus(error.status);
  return false;
}

const WAITS_MS = [15_000, 60_000, 300_000, 900_000];

/** How long to leave an Inspection after its `attempts`-th failure in a row. */
export const waitAfter = (attempts: number): number =>
  WAITS_MS[Math.min(Math.max(attempts, 1), WAITS_MS.length) - 1];
